import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface PortalProps {
  children: ReactNode;
}

interface PortalHostRenderArgs {
  containerRef: (node: HTMLDivElement | null) => void;
  children?: ReactNode;
  className?: string;
}

interface PortalHostProps {
  children?: ReactNode;
  className?: string;
  render: (args: PortalHostRenderArgs) => ReactNode;
}

export function createPortalSystem(name: string) {
  const PortalContext = createContext<HTMLDivElement | null>(null);
  PortalContext.displayName = `${name}Context`;

  function usePortalContainer(): HTMLDivElement | null {
    return useContext(PortalContext);
  }

  function Portal({ children }: PortalProps) {
    const container = usePortalContainer();
    if (container === null) {
      return null;
    }
    return createPortal(children, container);
  }

  function PortalHost({ children, className, render }: PortalHostProps) {
    const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(null);
    const containerRef = useCallback((node: HTMLDivElement | null) => {
      setContainerElement(node);
    }, []);

    return (
      <PortalContext.Provider value={containerElement}>
        {render({ containerRef, children, className })}
      </PortalContext.Provider>
    );
  }

  return {
    PortalContext,
    Portal,
    PortalHost,
    usePortalContainer,
  };
}
